import { Boxes, Layers, LayoutDashboard, Package2, ShoppingBag, ShoppingCart } from "lucide-react";
import type { ReactNode } from "react";

import { ROUTES } from "@/app/routes";

export type NavEntry = {
  to: string;
  label: string;
  icon: ReactNode;
  children?: Array<{ to: string; label: string }>;
};

/** Keep the production chain in task order: materials → stocks → recipes → products → sales. */
export const MAIN_NAV: NavEntry[] = [
  {
    to: ROUTES.dashboard,
    label: "Tableau de bord",
    icon: <LayoutDashboard size={20} />,
  },
  {
    to: ROUTES.provisions,
    label: "Matières premières",
    icon: <Package2 size={20} />,
  },
  {
    to: ROUTES.products,
    label: "Produits",
    icon: <ShoppingBag size={20} />,
  },
  {
    to: ROUTES.recipes,
    label: "Recettes",
    icon: <Boxes size={20} />,
  },
  {
    to: ROUTES.provisionStocks,
    label: "Stocks",
    icon: <Layers size={20} />,
    children: [
      { to: ROUTES.provisionStocks, label: "Stock provisionnel" },
      { to: ROUTES.productStocks, label: "Stock marchand" },
    ],
  },
  {
    to: ROUTES.vending,
    label: "Caisse",
    icon: <ShoppingCart size={20} />,
  },
];

/**
 * Mobile keeps the frequent actions under the thumb. The overflow keeps the
 * complete production chain discoverable, including the second stock view.
 */
const MOBILE_PRIMARY_PATHS: ReadonlySet<string> = new Set([ROUTES.dashboard, ROUTES.provisionStocks, ROUTES.vending]);

export const MOBILE_NAV = MAIN_NAV.filter((entry) => MOBILE_PRIMARY_PATHS.has(entry.to));
export const MOBILE_MORE_NAV: NavEntry[] = MAIN_NAV.filter((entry) => !MOBILE_PRIMARY_PATHS.has(entry.to)).concat(
  MAIN_NAV.flatMap((entry) =>
    (entry.children ?? [])
      .filter((child) => !MOBILE_PRIMARY_PATHS.has(child.to))
      .map((child) => ({ to: child.to, label: child.label, icon: entry.icon })),
  ),
);

export function isRouteActive(pathname: string, to: string): boolean {
  /** The dashboard sits at the root, which prefixes every other path. */
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function isEntryActive(pathname: string, entry: NavEntry): boolean {
  if (isRouteActive(pathname, entry.to)) return true;
  return (entry.children ?? []).some((child) => isRouteActive(pathname, child.to));
}
